<template>
  <div class="bg-white dark-bg-custom p-4 rounded-md">
    <div class="w-full mb-4 flex justify-between items-center">
      <h2 class="text-md font-semibold">Asset Classification & Provisioning</h2>
      <v-btn text color="primary" class="normal-case px-2" @click="addRow">
        <v-icon left>mdi-plus</v-icon> Add Classification
      </v-btn>
    </div>

    <div
      v-if="assetClassification && assetClassification.length > 0"
      class="mb-6 p-4 border rounded-lg bg-gray-50 bg-white dark-bg-custom"
    >
      <div
        class="grid grid-cols-12 gap-4 mb-2 font-semibold text-xs uppercase text-gray-500 px-2"
      >
        <div class="col-span-2">Key</div>
        <div class="col-span-3">Category</div>
        <div class="col-span-2">DPD Start</div>
        <div class="col-span-2">DPD End</div>
        <div class="col-span-2">Provisioning %</div>
        <div class="col-span-1 text-right">Action</div>
      </div>

      <div
        v-for="(item, index) in assetClassification"
        :key="`asset-${index}`"
        class="grid grid-cols-12 gap-4 mb-3 items-center px-1"
      >
        <div class="col-span-2">
          <v-text-field
            v-model="item.key"
            outlined
            dense
            hide-details="auto"
            placeholder="Key"
            disabled
          ></v-text-field>
        </div>
        <div class="col-span-3">
          <v-text-field
            v-model="item.category"
            outlined
            dense
            hide-details="auto"
            placeholder="Category Name"
            @input="generateKey(item)"
          ></v-text-field>
        </div>
        <div class="col-span-2">
          <v-text-field
            v-model.number="item.minDpd"
            type="number"
            outlined
            dense
            hide-details="auto"
            placeholder="Min DPD"
            @input="generateKey(item)"
          ></v-text-field>
        </div>
        <div class="col-span-2">
          <v-text-field
            v-model.number="item.maxDpd"
            type="number"
            outlined
            dense
            hide-details="auto"
            placeholder="Max DPD"
            @input="generateKey(item)"
          ></v-text-field>
        </div>
        <div class="col-span-2">
          <v-text-field
            v-model.number="item.provisioningPercentage"
            type="number"
            outlined
            dense
            hide-details="auto"
            suffix="%"
            placeholder="%"
          ></v-text-field>
        </div>
        <div class="col-span-1 text-right">
          <v-btn icon small color="error" @click="removeRow(index)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
    <div v-else class="mb-6 p-8 border-2 border-dashed rounded-lg text-center text-gray-400">
      No asset classification settings defined. Click "Add Classification" to start.
    </div>

    <div class="flex justify-end w-full mt-4">
      <v-btn
        :color="$vuetify.theme.currentTheme.primary"
        class="white--text normal-case"
        @click="saveSettings"
        :loading="loading"
      >
        <v-icon left>mdi-content-save</v-icon>
        Save Asset Settings
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AssetClassification',
  props: {
    orgData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      assetClassification: [],
      loading: false
    }
  },
  watch: {
    assetClassification: {
      handler(val) {
        this.$emit('assetData', val)
      },
      deep: true
    }
  },
  methods: {
    addRow() {
      this.assetClassification.push({
        key: '',
        category: '',
        minDpd: 0,
        maxDpd: 0,
        provisioningPercentage: 0
      })
    },
    removeRow(index) {
      this.assetClassification.splice(index, 1)
    },
    async fetchAssetClassification() {
      this.loading = true
      try {
        const response = await this.$axios.get('/orgSetup/asset-classification-provisioning')
        if (response.data?.rules) {
          this.assetClassification = response.data.rules
        } else if (response.data?.data) {
          this.assetClassification = response.data.data
        }
      } catch (error) {
        console.error('Error fetching asset classification:', error)
      } finally {
        this.loading = false
      }
    },
    generateKey(item) {
      if (item.category) {
        const cleanName = item.category.trim().toLowerCase().replace(/\s+/g, '_')
        item.key = `${cleanName}_${item.minDpd}_${item.maxDpd}`
      }
    },
    async saveSettings() {
      this.loading = true
      try {
        const payload = {
          rules: this.assetClassification
        }

        const response = await this.$axios.post('/orgSetup/asset-classification-provisioning', payload)

        if (response.data.success) {
          this.$toast.success('Asset classification updated successfully')
          this.$emit('refetchData')
        }
      } catch (error) {
        console.error('Error saving asset classification:', error)
        this.$toast.error('Failed to update asset classification')
      } finally {
        this.loading = false
      }
    }
  },
  async mounted() {
    await this.fetchAssetClassification()
  }
}
</script>
